import { Box, IconButton, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import CloseIcon from '@mui/icons-material/Close';

/**
 * Cabeçalho padrão dos modais do cardápio: barra com título centralizado, botão
 * de fechar (X) à direita e uma linha divisória embaixo. Fica fixo no topo do
 * Dialog (o conteúdo rola abaixo).
 *
 * Slots:
 *  - `inicio`: à esquerda do título (ex.: botão voltar do checkout);
 *  - `acao`: à direita, antes do X (ex.: botão "Limpar" da busca);
 *  - `children`: substitui o título por um conteúdo custom (ex.: campo de busca).
 *
 * Omita `aoFechar` para um cabeçalho sem X (ex.: confirmação de pedido no TOTEM).
 *
 * Exceção do padrão: a tela de detalhes do produto não usa este cabeçalho — lá
 * fica apenas o X flutuante sobre a foto.
 */
export function CabecalhoModal({
  titulo,
  aoFechar,
  inicio,
  acao,
  children,
}: {
  titulo?: ReactNode;
  aoFechar?: () => void;
  inicio?: ReactNode;
  acao?: ReactNode;
  children?: ReactNode;
}): JSX.Element {
  return (
    <Box
      sx={{
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        minHeight: 56,
        px: 1,
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      {/* Esquerda: slot opcional (mantém largura fixa p/ o título ficar centrado). */}
      {inicio && (
        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, minWidth: 40 }}>{inicio}</Box>
      )}

      {/* Centro: conteúdo custom OU título centralizado. */}
      {children ? (
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>{children}</Box>
      ) : (
        <Typography variant="h6" noWrap sx={{ flexGrow: 1, textAlign: 'center' }}>
          {titulo}
        </Typography>
      )}

      {/* Direita: ação opcional + fechar. */}
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, minWidth: 40, justifyContent: 'flex-end' }}
      >
        {acao}
        {aoFechar && (
          <IconButton aria-label="Fechar" onClick={aoFechar} sx={{ color: 'text.secondary' }}>
            <CloseIcon />
          </IconButton>
        )}
      </Box>
    </Box>
  );
}
